import {
  signInWithEmailAndPassword,
  signInAnonymously,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile as firebaseUpdateProfile,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
} from 'firebase/auth';
import { auth } from './firebase';
import { useAuthStore } from './stores/authStore';
import { useLobbyStore } from './stores/lobbyStore';
import { usePmBalanceStore } from './stores/pmBalanceStore';
import { usePromoBannerStore } from './stores/promoBannerStore';
import { apiClient } from './api';
import { websocketService } from './websocket-service';
import {
  track,
  clearUser,
  fireXConversion,
  fireMetaConversion,
  fireGuestStartOnce,
} from './analytics';
import { toast } from './toast';

/** sessionStorage key for a captured `?ref=CODE`. Shared with `App.tsx`'s capture effect. */
export const REFERRAL_CODE_STORAGE_KEY = 'referral-code';

/**
 * Redeem a friend's referral code captured earlier in this session (see
 * `App.tsx`'s `?ref=` capture), if one exists. Called exactly once, right
 * after a guest→real conversion or a first-time Google sign-up completes,
 * never from a logged-in account-settings screen (see the referral spec,
 * decision #11). The code is consumed from sessionStorage regardless of
 * outcome, since this is the only redemption chance the user gets.
 *
 * Never throws: an invalid/expired/already-used code, a self-referral
 * attempt, or a network failure (after `fetchJson`'s built-in retry) is
 * logged server-side only. It must never block or delay the signup flow
 * that already completed by the time this runs.
 */
async function redeemPersistedReferralCode(): Promise<void> {
  const code = sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
  if (!code) return;
  sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);

  try {
    const { bonusPm } = await apiClient.redeemInviteCode(code);
    toast.success(`+${bonusPm} PMT from a friend's invite!`);
  } catch (err) {
    console.error('Referral redeem failed (non-fatal):', err);
  }
}

/**
 * Clear the game-session route hints in localStorage. ProtectedRoute reads
 * `active-game-session` and auto-redirects to `/play/<id>`, so these must be
 * dropped whenever a FRESH identity takes over the tab — otherwise a brand-new
 * guest inherits a prior occupant's session id and gets bounced into a `/play`
 * route it isn't a participant of. Used by signOut and startGuest.
 *
 * NOT called from signInWithEmail/signInWithGoogle: a returning user signing
 * back in should keep their resume hint (the cross-user case there is only a
 * harmless WS-rejected bounce back to the lobby, not a data leak).
 */
const clearGameSessionRouteHints = () => {
  localStorage.removeItem('active-game-session');
  localStorage.removeItem('game-session-party');
  localStorage.removeItem('game-session-module');
  localStorage.removeItem('game-session-ws-url');
  localStorage.removeItem('game-session-duel-portraits');
  localStorage.removeItem('game-session-hero-video');
  localStorage.removeItem('game-session-hero-image');
  localStorage.removeItem('game-session-last-summary');
  localStorage.removeItem('game-last-session-id');
};

export const signInWithEmail = async (email: string, pass: string) => {
  const result = await signInWithEmailAndPassword(auth, email, pass);
  // Backfill display name for users who signed up before it was set automatically
  if (!result.user.displayName && email) {
    const displayName = email.split('@')[0];
    await firebaseUpdateProfile(result.user, { displayName });
    await result.user.reload();
  }
  // Reset lobby state to prevent previous user's data from leaking
  useLobbyStore.getState().reset();
  apiClient.clearAll();
  useAuthStore.getState().setUser(result.user);
  return result.user;
};

/**
 * Start a guest (anonymous) session: sign in anonymously, set the chosen username
 * as the Firebase `displayName`, then provision the backend profile + starter gift
 * EXPLICITLY via `POST /api/guest/start` (Turnstile-gated). The username is sent in
 * the request body, so the assigned name never depends on the timing of the token
 * write (the old lazy-on-read path could provision before the name landed and bake
 * "traveler"). The caller navigates to the lobby afterward.
 *
 * startGuest OWNS the guest bring-up — provision → seed balance → `setUser`. App.tsx
 * defers `setUser` for a brand-new guest (its read-only GET 404s until we've
 * provisioned), so the lobby never renders before the gift exists (no 0-balance
 * flash). The forced token refresh is load-bearing: the lobby derives the display
 * name from the token's `name` claim.
 */
export const startGuest = async (username: string, turnstileToken?: string) => {
  const { user } = await signInAnonymously(auth);
  await firebaseUpdateProfile(user, { displayName: username });
  await user.getIdToken(true); // refresh so the username rides the token (lobby name)
  // Fresh identity in this tab — clear any prior user's lobby + cached responses,
  // and drop stale game-session route hints so we don't bounce this brand-new
  // guest into a previous occupant's /play/<session> route.
  useLobbyStore.getState().reset();
  apiClient.clearAll();
  clearGameSessionRouteHints();

  const profile = await apiClient.guestStart(username, turnstileToken);

  // Guest account provisioned + gift granted: the paid-funnel conversion for the
  // guest LP. X-only — `signup_complete` fires only on guest→full convert, so this
  // is the event that attributes a "picked a name and played" guest to ad spend.
  // (GA4's `sign_up(anonymous, guest_start)` is emitted server-side by guest_start.)
  // Keyed on uid so a post-provision failure + retry can't double-count this guest.
  fireGuestStartOnce(user.uid);

  if (typeof profile.pmTokenBalance === 'number') {
    usePmBalanceStore.getState().setPmBalance(profile.pmTokenBalance);
  }
  // Mirror the assigned (possibly suffixed, e.g. 'avive2') name into Firebase Auth
  // so the lobby's token `name` claim matches the canonical username.
  if (profile.displayName && profile.displayName !== user.displayName) {
    await firebaseUpdateProfile(user, { displayName: profile.displayName });
    await user.getIdToken(true);
  }
  useAuthStore.getState().setUser(auth.currentUser);
};

/**
 * Create a real (non-anonymous) account directly, then sign the client into it.
 * Used at the referral-link signup entry point (`SignupPage`'s invited view) so
 * a friend's `?ref=` bonus redeems immediately instead of being gambled on a
 * future guest→real conversion. `POST /api/auth/signup` only creates the
 * account server-side; it does not establish a client session, so
 * `signInWithEmailAndPassword` follows immediately with the same credentials.
 *
 * This is a fresh identity taking over the tab (same class as `startGuest`,
 * not a conversion), so it gets the same session-hygiene reset.
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  username?: string,
  turnstileToken?: string
) => {
  const profile = await apiClient.signUpWithEmail(email, password, username, turnstileToken);
  const { user } = await signInWithEmailAndPassword(auth, email, password);

  useLobbyStore.getState().reset();
  apiClient.clearAll();
  clearGameSessionRouteHints();

  if (typeof profile.pmTokenBalance === 'number') {
    usePmBalanceStore.getState().setPmBalance(profile.pmTokenBalance);
  }
  useAuthStore.getState().setUser(user);
  await apiClient.getMyProfile().catch(() => {}); // hydrate the canonical username; non-fatal
  void redeemPersistedReferralCode(); // fire-and-forget, must never delay signup completion
  return user;
};

/**
 * Convert a guest into a full email+password account by linking credentials to
 * the SAME anonymous uid — every character / session / token balance carries
 * over with zero migration.
 *
 * Ordering is mandatory (see plan): link → force token refresh → `setUser`
 * (flips `isAnonymous`, refreshes the persisted store) → re-fetch `get_me`
 * (heals `profile.email`). `onAuthStateChanged` does NOT reliably refire on
 * link, so without the explicit refresh+setUser the converted user still looks
 * anonymous (convert card persists, buy gate keeps 403ing).
 */
export const convertToAccount = async (email: string, password: string) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');

  const credential = EmailAuthProvider.credential(email.trim().toLowerCase(), password);
  try {
    await linkWithCredential(user, credential);
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code === 'auth/email-already-in-use' || code === 'auth/credential-already-in-use') {
      // Linking failed because the email is already a separate account. We cannot
      // merge the two yet, so warn explicitly that signing into the existing
      // account leaves THIS guest hero behind (it lives only on the anonymous
      // profile). Do NOT silently push them to "sign in" and lose the character.
      throw new Error(
        'That email is already registered. Signing into that account will not bring this guest character with you (it lives only on this guest profile). To keep this hero, save it with a different email.'
      );
    }
    throw err;
  }

  await user.getIdToken(true); // refresh so the token carries the linked email (the provider claim stays "anonymous" on this session — the backend keys its guest check on the absent-email signal, not the provider)
  // Same uid — do NOT reset the lobby store (keep their in-flight session).
  // Bust the response cache so the next get_me reflects the new email.
  apiClient.clearAll();
  useAuthStore.getState().setUser(auth.currentUser);
  await apiClient.getMyProfile().catch(() => {}); // heal profile.email; non-fatal
  fireXConversion('signup_complete');
  fireMetaConversion('signup_complete');
  void redeemPersistedReferralCode(); // fire-and-forget, must never delay signup completion
  return auth.currentUser;
};

/** Friendly message when a Google account is already a full Polymyth account. */
function googleConflictError(err: unknown): Error {
  const code = (err as { code?: string }).code;
  if (
    code === 'auth/credential-already-in-use' ||
    code === 'auth/email-already-in-use' ||
    code === 'auth/account-exists-with-different-credential'
  ) {
    // The Google identity already belongs to a separate account; we cannot merge
    // them yet. Warn that signing into it abandons THIS guest hero rather than
    // silently sending them to a different uid where the character is gone.
    return new Error(
      'This Google account is already registered. Signing into that account will not bring this guest character with you. To keep this hero, save it with an email and password instead.'
    );
  }
  return err instanceof Error ? err : new Error('Google sign-in failed. Please try again.');
}

/**
 * Sign in (or sign up) with Google via popup. Popup, NOT redirect: the redirect
 * flow delivers its result through a cross-site `__/auth/iframe` on the authDomain
 * (polymyth.quest), which Chrome partitions whenever the app runs on a different
 * site (e.g. localhost) — `getRedirectResult` then resolves null and sign-in
 * silently never completes. The popup is a first-party top-level context and
 * returns the credential via `postMessage`, so it works on every origin. The
 * button click is the required user gesture, so `signInWithPopup` must be the
 * first await (no async work before it, or the browser blocks the popup).
 */
export const signInWithGoogle = async () => {
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  useLobbyStore.getState().reset();
  apiClient.clearAll();
  useAuthStore.getState().setUser(result.user);
  await apiClient.getMyProfile().catch(() => {}); // hydrate/provision; non-fatal
  // Firebase's standard "just created" heuristic: creationTime === lastSignInTime
  // only on the account's very first sign-in. Redeem only then, never on a
  // returning user's normal Google sign-in (see the referral spec, decision #11).
  if (result.user.metadata.creationTime === result.user.metadata.lastSignInTime) {
    void redeemPersistedReferralCode(); // fire-and-forget, must never delay sign-in completion
  }
  return result.user;
};

/**
 * Convert a guest into a full account by linking Google to the SAME anonymous uid
 * (same data-preservation as `convertToAccount`, one click, no password). Popup
 * (see `signInWithGoogle`) so the link resolves inline on localhost + deployed.
 */
export const convertWithGoogle = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');

  try {
    await linkWithPopup(user, new GoogleAuthProvider());
  } catch (err) {
    throw googleConflictError(err);
  }

  await user.getIdToken(true); // refresh so the token carries the linked Google email
  apiClient.clearAll(); // same uid — keep the lobby store; just bust the response cache
  useAuthStore.getState().setUser(auth.currentUser);
  await apiClient.getMyProfile().catch(() => {}); // heal profile.email; non-fatal
  fireXConversion('signup_complete');
  fireMetaConversion('signup_complete');
  void redeemPersistedReferralCode(); // fire-and-forget, must never delay conversion completion
  return auth.currentUser;
};

export const signOut = async () => {
  // Disconnect websocket and clean up server-side sessions first
  websocketService.logout();
  // Reset lobby store state (clears characters, chat, parties, etc.)
  useLobbyStore.getState().reset();
  // Clear promo-banner status + dismissals so the next identity in this tab
  // doesn't inherit this user's banner state.
  usePromoBannerStore.getState().reset();
  // Sign out from Firebase — track AFTER so a thrown signout doesn't emit a phantom logout event
  await firebaseSignOut(auth);
  track('logout');
  clearUser();
  // Clear API cache
  apiClient.clearAll();
  // Clear auth store state
  useAuthStore.getState().signOut();
  // Clear all game-session localStorage to prevent data leaking to next user
  clearGameSessionRouteHints();
  localStorage.removeItem('game-storage');
  localStorage.removeItem('lobby-active-chat-room');
  localStorage.removeItem('lobby-storage');
  localStorage.removeItem('polymyth:recentGameCommands');
  // A captured `?ref=` code must not survive a sign-out into a different identity
  // in the same tab (e.g. logged-in user clicks a friend's link, signs out, then
  // signs up as a guest, which would wrongly redeem the stale code for them).
  sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
};

/**
 * Atomically update the user profile across Firebase Auth, Firestore (via
 * `PATCH /api/users/me`), and the Zustand auth store.
 *
 * Write order: Firebase Auth first, then Firestore, then Zustand. If the
 * Firebase Auth write fails we throw and nothing else mutates. If the
 * Firestore write fails we surface a "sync pending" warning via the caller's
 * catch but the Firebase update sticks — `get_me` email backfill or the next
 * manual save reconciles. Zustand is updated last so the UI only reflects
 * committed state.
 */
export const updateUserProfile = async (updates: { displayName?: string; photoURL?: string }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');

  // The backend assigns the canonical (unique) username, so PATCH Firestore FIRST
  // and mirror the value it returns (a collision rename 'bob' → 'bob2') into
  // Firebase Auth — never the reverse, or Auth would hold a name the server rejected.
  const firestorePayload: { displayName?: string; avatarUrl?: string } = {};
  if (updates.displayName !== undefined) firestorePayload.displayName = updates.displayName;
  if (updates.photoURL !== undefined) firestorePayload.avatarUrl = updates.photoURL;

  let finalDisplayName = updates.displayName;
  if (Object.keys(firestorePayload).length > 0) {
    const res = await apiClient.patchMe(firestorePayload);
    // Only adopt the server's canonical name when we actually requested a rename,
    // so a photo-only update never rewrites the display name.
    if (updates.displayName !== undefined && res.displayName) finalDisplayName = res.displayName;
  }

  const authUpdates: { displayName?: string; photoURL?: string } = {};
  if (finalDisplayName !== undefined) authUpdates.displayName = finalDisplayName;
  if (updates.photoURL !== undefined) authUpdates.photoURL = updates.photoURL;
  if (Object.keys(authUpdates).length > 0) {
    await firebaseUpdateProfile(user, authUpdates);
  }

  useAuthStore.getState().updateProfile({
    ...updates,
    ...(finalDisplayName !== undefined ? { displayName: finalDisplayName } : {}),
  });
  return finalDisplayName;
};

/**
 * Delete the caller's account: DELETE /api/users/me with `{ confirmEmail }`,
 * then sign out locally. The backend removes Firebase Auth + cascades Firestore.
 * We do NOT call `apiClient.signOut`-style helpers here because that would
 * redirect; callers own the redirect.
 */
export async function deleteAccount(confirmEmail: string): Promise<void> {
  await apiClient.deleteAccount(confirmEmail);
  // Firebase Auth is already gone on the backend. Cleaning up local state
  // here avoids an auto-401 loop from the axios/fetch client reading a stale
  // token before the next render.
  websocketService.logout();
  useLobbyStore.getState().reset();
  usePromoBannerStore.getState().reset();
  apiClient.clearAll();
  await firebaseSignOut(auth).catch(() => {
    // Firebase SDK may throw "auth/user-token-expired" because the backend
    // already invalidated the user. Swallow — state is being cleared anyway.
  });
  useAuthStore.getState().signOut();
}
